import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { SIGNALS_PATH_NAME } from './signals.constants';
import { SignalSchema } from '@terra/shared-schemas-orchestrator';
import { SignalHttpCreateInputSchema } from './signals-http-create.schema';
import { SignalUpdateInputSchema } from './signals-update.schema';
import { z } from 'zod';
import { PaginationQueryParamsSchema } from '@terra/shared-dto-types';
import {
  SignalsApplicationIdParamsSchema,
  SignalsSearchQuerySchema,
  SignalsSearchByApplicationBodySchema,
  GroupedSignalsSearchBodySchema,
  SignalQualityDistributionSchema,
  SignalCategoryDistributionSchema,
  SignalTrendsOverTimeSchema,
  GroupedSignalsResponseSchema,
  SignalGroupsSearchBodySchema,
  SignalGroupsSummaryResponseSchema,
  SignalGroupPageSearchBodySchema,
  SignalGroupPageResponseSchema,
  SignalsFlatResponseSchema,
  BulkUpdateSignalsPayloadSchema,
  BulkUpdateSignalsResponseSchema,
  BulkDeleteSignalsPayloadSchema,
  BulkDeleteSignalsResponseSchema,
  TopEndpointsBySignalsSchema,
  GroupedSignalSchema,
  SignalWithHarEntryRequestSchema,
} from './signals.schema';
import { SignalAuditLogsResponseSchema } from './audit.schemas';
import {
  GetSignalSourceFilterOptionsParamsSchema,
  SignalSourceFilterOptionsSchema,
} from './signal-source-filter-options.schema';
import { signalsAnalyticsProcedures } from './signals-analytics.procedures';
import { signalsCountProcedures } from './signals-count.procedures';

export const SignalsContract = terraContract
  .prefix(`/${SIGNALS_PATH_NAME}`)
  .router({
    getAllSignals: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all signals',
        description:
          'Retrieves all security signals (potential vulnerabilities) across all applications for the tenant. Signals are generated by AI analysis of endpoints and represent potential security issues that need investigation.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: PaginationQueryParamsSchema }))
      .output(SignalWithHarEntryRequestSchema.array()),
    getSignal: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}',
        summary: 'Get signal details',
        description:
          'Retrieves complete details for a single security signal including its category, confidence level, associated endpoint, and testing status.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: z.object({ id: z.string() }) }))
      .output(GroupedSignalSchema),
    createSignal: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create signal',
        description:
          'Manually creates a new security signal. This is typically used when security researchers identify potential vulnerabilities outside of automated discovery.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: SignalHttpCreateInputSchema }))
      .output(SignalSchema),
    updateSignal: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}',
        summary: 'Update signal',
        description: 'Updates user-editable signal fields (label, assignee)',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          body: SignalUpdateInputSchema,
        }),
      )
      .output(SignalSchema),
    updateSignalsBulk: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'PUT',
        path: '/bulk',
        summary: 'Bulk update signals',
        description:
          'Updates multiple signals at once. Useful for batch operations like marking multiple signals as reviewed or assigning them to a team member.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkUpdateSignalsPayloadSchema }))
      .output(BulkUpdateSignalsResponseSchema),
    deleteSignalsBulk: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'DELETE',
        path: '/bulk-delete',
        summary: 'Bulk delete signals',
        description:
          'Permanently deletes multiple signals. Use with caution as this action cannot be undone.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: BulkDeleteSignalsPayloadSchema }))
      .output(BulkDeleteSignalsResponseSchema),
    getSignalsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}',
        summary: 'List signals by application',
        description:
          'Retrieves all signals for a specific application with optional filters and totalCount. Supports filtering by label (HOT/COLD/PASSED), category, confidence level, date ranges, and server-side sorting via orderBy. Use urlPatterns for hierarchical URL filtering.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ applicationId: z.string() }),
          query: SignalsSearchQuerySchema,
        }),
      )
      .output(SignalsFlatResponseSchema),
    searchSignalsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/search',
        summary: 'Search signals by application (request body)',
        description:
          'Same response as GET /application/:applicationId but accepts filters and pagination in the JSON body. Use this when query strings would exceed URL length limits (e.g. many category values).',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SignalsApplicationIdParamsSchema,
          body: SignalsSearchByApplicationBodySchema,
        }),
      )
      .output(SignalsFlatResponseSchema),
    getGroupedSignalsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/grouped',
        summary: 'List signals grouped by endpoint',
        description:
          'Retrieves signals organized by their associated endpoint. Ideal for displaying signals in a hierarchical tree view where endpoints are parent nodes. Use urlPatterns for efficient URL-based filtering with regex support.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ applicationId: z.string() }),
          query: GroupedSignalsSearchBodySchema,
        }),
      )
      .output(GroupedSignalsResponseSchema),
    searchGroupedSignalsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/grouped/search',
        summary: 'Search grouped signals by application (request body)',
        description:
          'Same response as GET /application/:applicationId/grouped but accepts filters and pagination in the JSON body. Prefer this when filtering by componentTypes or other fields that should not depend on query-string array parsing.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SignalsApplicationIdParamsSchema,
          body: GroupedSignalsSearchBodySchema,
        }),
      )
      .output(GroupedSignalsResponseSchema),
    searchSignalGroupsByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/groups/search',
        summary: 'Search signal group summaries by application',
        description:
          'Returns paginated group headers (endpoint or asset) with signal counts. Fetch signal rows per group via groups/signals/search.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SignalsApplicationIdParamsSchema,
          body: SignalGroupsSearchBodySchema,
        }),
      )
      .output(SignalGroupsSummaryResponseSchema),
    searchSignalGroupPageByApplicationId: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/groups/signals/search',
        summary: 'Search signals within one group',
        description:
          'Returns one page of signals for a single endpoint or asset group. Use with groups/search for lazy-loaded signal lists.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: SignalsApplicationIdParamsSchema,
          body: SignalGroupPageSearchBodySchema,
        }),
      )
      .output(SignalGroupPageResponseSchema),
    ...signalsCountProcedures,
    getSignalSourceFilterOptions: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/application/{applicationId}/source-filter-options',
        summary: 'Get signal source filter options',
        description:
          'Returns the distinct sourceLabel and importBatchId values present on this application\u2019s signals, for populating Signals list filter dropdowns.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: GetSignalSourceFilterOptionsParamsSchema }))
      .output(SignalSourceFilterOptionsSchema),
    ...signalsAnalyticsProcedures,
    getAuditLogs: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_TESTS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/audit-logs',
        summary: 'Get signal audit logs',
        description:
          'Retrieves the change history for a specific signal, including who made the change (user or AI agent), what changed, and when.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({ id: z.string() }),
          query: PaginationQueryParamsSchema,
        }),
      )
      .output(SignalAuditLogsResponseSchema),
    triggerSignalGenerationFromCandidates: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/signal-candidates/trigger-generation',
        summary:
          'Trigger signal generation from signal candidates; optionally migrate cold/blocked signals to candidates first',
        description:
          'With only applicationId: migrates cold/blocked signals to candidates for a batch of endpoints (use migrationLimit and migrationSkip to paginate, e.g. first 100: skip=0, limit=100), then processes all candidates. With shouldMigrateSignals and endpointId: migrates that single endpoint only, then processes. Pagination applies only when migrating by application.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
            targetId: z.string().optional(),
          }),
          body: z.object({
            shouldMigrateSignals: z.boolean().optional(),
            endpointId: z.string().optional(),
            migrationLimit: z.int().min(1).max(500).optional(),
            migrationSkip: z.int().min(0).optional(),
          }),
        }),
      )
      .output(
        z.object({
          message: z.string(),
          endpointsProcessed: z.number().optional(),
          candidatesProcessed: z.number().optional(),
          signalsMigrated: z.number().optional(),
          endpointsMigrationBatchSize: z.number().optional(),
        }),
      )
      .errors({
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    processStaleSignalCandidatesNow: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/signal-candidates/process-stale-now',
        summary: 'Process stale signal candidates immediately',
        description:
          'Triggers immediate stale signal candidates processing for a specific application without waiting for the scheduled 7-day threshold window.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: z.object({ applicationId: z.string() }),
          body: z.object({}),
        }),
      )
      .output(
        z.object({
          message: z.string(),
          totalProcessed: z.number(),
          totalFailed: z.number(),
        }),
      )
      .errors({
        INTERNAL_SERVER_ERROR: {
          status: 500,
          data: z.object({ message: z.string() }),
        },
      }),
    runFullFlowForSignalCandidate: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/application/{applicationId}/signal-candidates/{candidateId}/run-full-flow',
        summary: 'Run full flow for a single signal candidate',
        description:
          "Runs the full pipeline for one signal candidate: validation LLM → security test case generation → signal creation with deduplication. Uses the candidate's endpoint and its code graph. Returns 202 when the flow is triggered; processing is async. Use 400 when the endpoint has no code graph; 404 when the candidate is not found.",
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
            targetId: z.string().optional(),
            candidateId: z.string(),
          }),
          body: z.object({}),
        }),
      )
      .output(z.object({ message: z.string() }))
      .errors({
        BAD_REQUEST: { status: 400, data: z.object({ message: z.string() }) },
        NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      }),
  });

export type SignalQualityDistribution = z.infer<
  typeof SignalQualityDistributionSchema
>;

export type SignalCategoryDistribution = z.infer<
  typeof SignalCategoryDistributionSchema
>;

export type TopEndpointsBySignals = z.infer<typeof TopEndpointsBySignalsSchema>;

export type SignalTrendsOverTime = z.infer<typeof SignalTrendsOverTimeSchema>;
